'use client'

import { useMemo } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useSseChannel } from '@/lib/sse/use-sse-channel'
import { getNotifications } from './notification-actions'
import type { NotificationData } from './types'

interface NotificationPage {
  items: NotificationData[]
  nextCursor: string | null
  hasMore: boolean
}

export function useNotifications(userId: string | undefined, filter: string = 'all') {
  const queryClient = useQueryClient()

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['notifications', filter],
    queryFn: async ({ pageParam }) => {
      const result = await getNotifications({
        cursor: pageParam,
        limit: 20,
        filter,
      })
      if (result.success && result.data) {
        return result.data as NotificationPage
      }
      return { items: [], nextCursor: null, hasMore: false } as NotificationPage
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!userId,
  })

  // Realtime: invalidate the list whenever a notification row changes
  // for this user. The trigger payload only includes { op, id, ... },
  // so the next refetch is authoritative.
  useSseChannel(
    userId ? `notifications:user:${userId}` : null,
    (event) => {
      if (event.type === 'message') {
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
      }
    },
    { enabled: !!userId },
  )

  const notifications = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  )

  return {
    notifications,
    isLoading,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    isFetchingNextPage,
  }
}
